import { FC, SetStateAction, useEffect, useState } from "react";
import { queryOrders } from "../../app/services/orderService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import { Order } from "../../models/responses/order";
import { Badge, Col, Pagination, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Form from "react-bootstrap/Form";
import moment from "moment-timezone";
import { getOrderStatusById } from "../../app/services/orderStatusService";
import { OrderStatus } from "../../models/responses/orderStatus";
import { ArrowDown, ArrowUp, Check, Eye, Plus } from "react-bootstrap-icons";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { setClientToggleLocked } from "../../app/slices/clientTypeSlice";

interface Props {}

const Orders: FC<Props> = () => {
  moment.locale("sv");
  moment.updateLocale(moment.locale(), { invalidDate: "-" });
  const navigate = useNavigate();

  const { orderStatusId } = useParams<{ orderStatusId: string }>();

  const loadingOrders: boolean = useSelector(
    (state: RootState) => state.order.isWaitingOrderGetResponse
  );

  const orders: Order[] = useSelector(
    (state: RootState) => state.order.queryOrderList || []
  );

  const orderStatus: OrderStatus = useSelector(
    (state: RootState) => state.orderStatus.orderStatus
  );

  const dispatch = useAppDispatch();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [queryText, setQueryText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [orderBy, setOrderBy] = useState("CreatedAt");
  const [orderDirection, setOrderDirection] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const total = useSelector((state: RootState) => state.order.totalQueryOrder);
  const totalPages = Math.ceil((total || 0) / pageSize);
  const clientType = useSelector(
    (state: RootState) => state.clientType.clientType
  );

  const handleSearch = () => {
    setQueryText(searchInput);
  };

  const handleSort = (field: SetStateAction<string>) => {
    const isDesc = orderBy === field && !orderDirection;
    setOrderBy(field);
    setOrderDirection(isDesc);
  };

  const handleKeyDown = (e: { key: string; preventDefault: () => void }) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };
  useEffect(() => {
    dispatch(
      queryOrders(
        instance,
        account,
        orderStatusId,
        page,
        pageSize,
        queryText,
        orderBy,
        orderDirection,
        true,
        clientType
      )
    );
  }, [
    account,
    accounts,
    dispatch,
    instance,
    orderStatusId,
    page,
    pageSize,
    queryText,
    orderBy,
    orderDirection,
    clientType,
  ]);

  useEffect(() => {
    return pollingForNewOrders();
  });

  useEffect(() => {
    dispatch(setClientToggleLocked(false));
    dispatch(getOrderStatusById(instance, account, orderStatusId));
  }, [account, accounts, dispatch, instance, orderStatusId]);

  const pollingForNewOrders = () => {
    const intervalId = setInterval(() => {
      dispatch(
        queryOrders(
          instance,
          account,
          orderStatusId,
          page,
          pageSize,
          queryText,
          orderBy,
          orderDirection,
          true,
          clientType
        )
      );
    }, 5000);
    return () => clearInterval(intervalId);
  };

  const openOrderHandler = (order: Order) => {
    navigate(`/orderStatus/${order.orderStatusId}/orders/view/${order.id}`, {
      relative: "path",
    });
  };

  const renderSortIcon = (field: string) => {
    if (orderBy === field) {
      return orderDirection ? <ArrowDown /> : <ArrowUp />;
    }
    return null;
  };

  return (
    <div>
      <Row className="mb-3">
        <Col xs={12} md={8}>
          <h1>{orderStatus.name}</h1>
        </Col>
        <Col xs={12} md={4}>
          <Button
            className="float-end mt-2"
            variant="outline-primary"
            href={`/orderStatus/${orderStatusId}/orders/add`}
          >
            Lägg till <Plus />
          </Button>
        </Col>
      </Row>
      <Form className="mb-3">
        <Row>
          <Col xs={12} md={4}>
            <Form.Control
              type="text"
              placeholder="Regnummer/IO-nummer/FO-nummer"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </Col>
          <Col xs={12} md={2}>
            <Button variant="outline-primary" onClick={handleSearch}>
              Sök
            </Button>
          </Col>
        </Row>
      </Form>
      <Table responsive striped bordered hover>
        <thead>
          <tr role="button">
            <th onClick={() => handleSort("Customer.Name")}>
              Kund {renderSortIcon("Customer.Name")}{" "}
            </th>
            <th onClick={() => handleSort("CreatedAt")}>
              Skapad {renderSortIcon("CreatedAt")}
            </th>
            <th onClick={() => handleSort("DeliveryDate")}>
              Leverans {renderSortIcon("DeliveryDate")}
            </th>
            <th onClick={() => handleSort("BookingDate")}>
              Prod.dag {renderSortIcon("BookingDate")}
            </th>
            <th onClick={() => handleSort("OrderType.Name")}>
              Ordertyp {renderSortIcon("OrderType.Name")}
            </th>
            <th onClick={() => handleSort("RegistrationNumber")}>
              Reg.nr {renderSortIcon("RegistrationNumber")}
            </th>
            {clientType === ClientType.Standard && (
              <th onClick={() => handleSort("AONumber")}>
                AO-nummer {renderSortIcon("AONumber")}
              </th>
            )}
            {clientType === ClientType.Catalog && (
              <th onClick={() => handleSort("ExternalOrderId")}>
                Order.nr {renderSortIcon("ExternalOrderId")}
              </th>
            )}
            {clientType === ClientType.Fleet && (
              <th onClick={() => handleSort("ClientExternalOrderId")}>
                Order.nr PN {renderSortIcon("ClientExternalOrderId")}
              </th>
            )}
            <th onClick={() => handleSort("Article.SalesOrderNumber")}>
              FO-nummer {renderSortIcon("Article.SalesOrderNumber")}
            </th>
            <th onClick={() => handleSort("UserName")}>
              Redigeras av {renderSortIcon("UserName")}
            </th>
            <th onClick={() => handleSort("Exported")}>
              Exporterad {renderSortIcon("Exported")}
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr
              key={order.id}
              role="button"
              onClick={() => openOrderHandler(order)}
            >
              <td>{order.customer?.name}</td>
              <td>
                {moment
                  .utc(order.createdAt)
                  .tz("Europe/Stockholm")
                  .format("yyyy-MM-DD HH:mm")}
              </td>
              <td>
                {moment
                  .utc(order.deliveryDate)
                  .tz("Europe/Stockholm")
                  .format("yyyy-MM-DD")}
              </td>
              <td>
                {moment
                  .utc(order.bookingDate)
                  .tz("Europe/Stockholm")
                  .format("yyyy-MM-DD")}
              </td>
              <td>{order.orderType?.name}</td>
              <td>{order.registrationNumber}</td>
              {clientType === ClientType.Standard && <td>{order.aoNumber}</td>}
              {clientType === ClientType.Catalog && <td>{order.externalOrderId}</td>}
              {clientType === ClientType.Fleet && <td>{order.clientExternalOrderId}</td>}
              <td>{order.salesOrderNumber}</td>
              <td>{order.userName}</td>
              <td>{order.exported && <Check className="text-success" />}</td>
              <td align="center">
                <Eye />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {loadingOrders && (
        <>
          <Row className="mb-3">
            <Col className="text-center">
              <Spinner variant="primary" />
            </Col>
          </Row>
        </>
      )}
      <Pagination className="justify-content-center">
        <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
        <Pagination.Prev
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
        />
        {[...Array(totalPages)].map((_, index) => {
          const pageIndex = index + 1;
          if (pageIndex >= page - 3 && pageIndex <= page + 3) {
            return (
              <Pagination.Item
                key={pageIndex}
                active={pageIndex === page}
                onClick={() => setPage(pageIndex)}
              >
                {pageIndex}
              </Pagination.Item>
            );
          }
          return null;
        })}
        <Pagination.Next
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
        />
        <Pagination.Last
          onClick={() => setPage(totalPages)}
          disabled={page === totalPages}
        />
      </Pagination>
    </div>
  );
};

export default Orders;
